import type { ChannelMaterialConfig, EditableConfig } from '@/types/channelMaterials';
import { channelMaterialsApi, parseMaterialContent } from '@/api/smartService/channelMaterials';

/** 抖音收集号码：发送配置中 materia_ids 的占位字符串（与后端约定，无对应素材实体） */
export const MSG_TYPE_PHONE_COLLECTION = 'MSG_TYPE_PHONE_COLLECTION';

/**
 * 飞鸽新版留资卡：发送配置中 materia_ids 的占位字符串（无对应素材实体）
 * 具体取值待后端确认，暂按收集号码的同款风格
 */
export const MSG_TYPE_NEW_LEAD_CARD = 'MSG_TYPE_NEW_LEAD_CARD';

/**
 * 统一渠道物料接口（type=9 小程序卡片）返回项 → 列表展示字段
 * content: { app_id, page_path, title, thumb_url|image_url }
 */
function transformMiniProgram(item: Record<string, unknown>): Record<string, unknown> {
  const c = parseMaterialContent(item.content);
  return {
    ...item,
    app_id: c.app_id ?? '',
    page_path: c.page_path ?? '',
    card_name: c.title ?? item.name ?? '',
    description: c.description ?? '',
    image_url: c.thumb_url ?? c.image_url ?? '',
  };
}

/**
 * 统一渠道物料接口（type=5 图文消息）返回项 → 列表展示字段
 * content: { title, description, url, image_url }
 */
function transformArticle(item: Record<string, unknown>): Record<string, unknown> {
  const c = parseMaterialContent(item.content);
  return {
    ...item,
    title: c.title ?? item.name ?? '',
    description: c.description ?? '',
    url: c.url ?? '',
    image_url: c.image_url ?? '',
  };
}

/**
 * 统一渠道物料接口（type=12 获客链接）返回项 → 列表展示字段
 * content: { url, title }
 */
function transformAcquisitionLink(item: Record<string, unknown>): Record<string, unknown> {
  const c = parseMaterialContent(item.content);
  return {
    ...item,
    title: c.title ?? item.name ?? '',
    address: c.url ?? '',
  };
}

/**
 * 将 /materials 接口返回的素材数据转为 MenuTemplate 结构
 * content: { header_guide, ending, auto_newline, auto_number, questions: [{sort,guide,question}] }
 */
export function transformMaterial(item: Record<string, unknown>): Record<string, unknown> {
  const content = (item.content ?? {}) as Record<string, unknown>;
  const questions = (content.questions ?? []) as {
    sort?: number;
    guide?: string;
    question?: string;
  }[];
  return {
    ...item,
    title: item.name ?? item.title,
    guide_words: content.header_guide ?? '',
    questions: JSON.stringify({
      questions: questions.map((q) => {
        const guide = q.guide ?? '';
        const question = q.question ?? '';
        return {
          title: guide && question ? `${guide}：${question}` : guide || question,
          guide,
          question,
        };
      }),
      conclusion: content.ending ?? '',
    }),
  };
}

/**
 * 将 /materials 接口返回的网站留资卡数据扁平化为 WebLeadCard 预览所需结构
 * content: { card_image, phone_label, button_text, ... }
 */
export function transformWebLeadCard(item: Record<string, unknown>): Record<string, unknown> {
  const content = (item.content ?? {}) as Record<string, unknown>;
  return {
    ...item,
    image_url: content.card_image ?? '',
    placeholder: content.phone_label ?? '',
    btn_text: content.button_text ?? '',
  };
}

// ── 可编辑配置（内联 CRUD）─────────────────────────────────────────────
// payload 形状参照访客会话对应弹框 onSave 函数，须与后端协议保持一致

/** 抖音小程序卡片（独立接口 dyMiniXxx，字段：mini_app_id/page_path/title/content/image_url） */
const dyMiniProgramEditable: EditableConfig = {
  fields: [
    {
      prop: 'mini_app_id',
      label: 'appid',
      type: 'input',
      placeholder: '以tt开头',
      required: true,
      validate: (v) => (v && !/^tt/.test(v) ? '小程序 appid 需以 tt 开头' : null),
    },
    { prop: 'page_path', label: '小程序路径', type: 'input', placeholder: '路径' },
    { prop: 'title', label: '卡片名称', width: 120, type: 'input', required: true },
    { prop: 'content', label: '卡片描述', width: 120, type: 'input', required: true },
    { prop: 'image_url', label: 'LOGO', width: 72, type: 'upload', required: true },
  ],
  emptyRow: () => ({
    mini_app_id: '',
    page_path: '',
    title: '',
    content: '',
    image_url: '',
    isNew: true,
  }),
  load: async ({ accountId, page, pageSize }) => {
    const res = (await channelMaterialsApi.dyMiniList({
      account_id: Number(accountId) || undefined,
      page,
      page_size: pageSize,
    })) as { list?: Record<string, unknown>[]; total?: number };
    return { list: res.list ?? [], total: res.total ?? 0 };
  },
  create: async (rows, accountId) => {
    const payload = rows.map((r) => ({
      account_id: Number(accountId),
      title: String(r.title ?? ''),
      content: String(r.content ?? ''),
      image_url: String(r.image_url ?? ''),
      mini_app_id: String(r.mini_app_id ?? ''),
      page_path: String(r.page_path ?? ''),
    }));
    const res = (await channelMaterialsApi.dyMiniCreate(payload)) as {
      results?: { ok: boolean; error?: string; index: number }[];
    };
    return {
      failures: (res?.results ?? [])
        .filter((r) => !r.ok)
        .map((f) => `新增第${f.index + 1}条：${f.error ?? ''}`),
    };
  },
  update: async (rows, accountId) => {
    const payload = rows.map((r) => ({
      id: r.id as number,
      account_id: Number(accountId) || undefined,
      title: String(r.title ?? ''),
      content: String(r.content ?? ''),
      image_url: String(r.image_url ?? ''),
      mini_app_id: String(r.mini_app_id ?? ''),
      page_path: String(r.page_path ?? ''),
    }));
    const res = (await channelMaterialsApi.dyMiniUpdate(payload)) as {
      results?: { ok: boolean; error?: string; index: number }[];
    };
    return {
      failures: (res?.results ?? [])
        .filter((r) => !r.ok)
        .map((f) => `更新第${f.index + 1}条：${f.error ?? ''}`),
    };
  },
  remove: async (ids) => {
    await channelMaterialsApi.dyMiniDelete(ids);
  },
  deleteConfirmText: '确定删除选中的小程序卡片吗？',
  addText: '添加小程序',
};

/** 微信公众号图文消息（type=5，content: {title, description, url, image_url}） */
const wxArticleEditable: EditableConfig = {
  fields: [
    { prop: 'title', label: '图文标题', type: 'input', required: true },
    { prop: 'description', label: '图文描述', type: 'input' },
    { prop: 'link_url', label: '图文链接', type: 'input', required: true },
    { prop: 'cover_url', label: '图文封面', width: 80, type: 'upload' },
  ],
  emptyRow: () => ({ title: '', description: '', link_url: '', cover_url: '', isNew: true }),
  load: async ({ accountId, page, pageSize }) => {
    const { list, total } = await channelMaterialsApi
      .list({ account_id: accountId, type: 5, page, per_page: pageSize })
      .catch(() => ({ list: [], total: 0 }));
    return {
      list: (list as Record<string, unknown>[]).map((r) => {
        const c = parseMaterialContent(r.content);
        return {
          id: r.id,
          title: (c.title as string) ?? (r.name as string) ?? '',
          description: (c.description as string) ?? '',
          link_url: (c.url as string) ?? '',
          cover_url: (c.image_url as string) ?? '',
        };
      }),
      total,
    };
  },
  create: async (rows, accountId) => {
    const failures: string[] = [];
    await Promise.all(
      rows.map(async (row, idx) => {
        try {
          await channelMaterialsApi.create({
            account_id: accountId,
            name: String(row.title ?? '') || '图文消息',
            type: 5,
            content: {
              title: row.title ?? '',
              description: row.description ?? '',
              url: row.link_url ?? '',
              image_url: row.cover_url ?? '',
            },
          });
        } catch (e) {
          failures.push(`新增第${idx + 1}条：${(e as Error)?.message ?? '失败'}`);
        }
      }),
    );
    return { failures };
  },
  update: async (rows) => {
    const failures: string[] = [];
    await Promise.all(
      rows.map(async (row, idx) => {
        try {
          await channelMaterialsApi.update(row.id as number, {
            name: String(row.title ?? '') || '图文消息',
            content: {
              title: row.title ?? '',
              description: row.description ?? '',
              url: row.link_url ?? '',
              image_url: row.cover_url ?? '',
            },
          });
        } catch (e) {
          failures.push(`更新第${idx + 1}条：${(e as Error)?.message ?? '失败'}`);
        }
      }),
    );
    return { failures };
  },
  remove: async (ids) => {
    await Promise.all(ids.map((id) => channelMaterialsApi.delete(id)));
  },
  deleteConfirmText: '确定删除选中的图文消息吗？',
  addText: '添加图文',
};

/**
 * 小程序卡片（通用接口 type=9）工厂
 * - wx 公众号：content 字段用 thumb_url，无 description
 * - wxkf/fmc：content 字段用 image_url，含 description
 */
function createMiniProgramEditable(opts: {
  imageContentKey: 'thumb_url' | 'image_url';
  withDescription: boolean;
  addText: string;
  deleteConfirmText: string;
}): EditableConfig {
  const baseFields: EditableConfig['fields'] = [
    { prop: 'app_id', label: 'appid', width: 120, type: 'input', required: true },
    { prop: 'page_path', label: '小程序路径', type: 'input', required: true },
    { prop: 'title', label: '卡片名称', width: 140, type: 'input', required: true },
  ];
  if (opts.withDescription) {
    baseFields.push({ prop: 'description', label: '卡片描述', width: 120, type: 'input' });
  }
  baseFields.push({
    prop: 'image_url',
    label: opts.withDescription ? 'LOGO' : '上传图片',
    width: opts.withDescription ? 80 : 100,
    type: 'upload',
    required: true,
  });

  const buildContent = (row: Record<string, unknown>) => {
    const c: Record<string, unknown> = {
      app_id: row.app_id ?? '',
      page_path: row.page_path ?? '',
      title: row.title ?? '',
    };
    c[opts.imageContentKey] = row.image_url ?? '';
    if (opts.withDescription) c.description = row.description ?? '';
    return c;
  };

  const baseRow: Record<string, unknown> = {
    app_id: '',
    page_path: '',
    title: '',
    image_url: '',
    isNew: true,
  };
  if (opts.withDescription) baseRow.description = '';

  return {
    fields: baseFields,
    emptyRow: () => ({ ...baseRow }),
    load: async ({ accountId, page, pageSize }) => {
      const { list, total } = await channelMaterialsApi
        .list({ account_id: accountId, type: 9, page, per_page: pageSize })
        .catch(() => ({ list: [], total: 0 }));
      return {
        list: (list as Record<string, unknown>[]).map((r) => {
          const c = parseMaterialContent(r.content);
          const row: Record<string, unknown> = {
            id: r.id,
            app_id: (c.app_id as string) ?? '',
            page_path: (c.page_path as string) ?? '',
            title: (c.title as string) ?? (r.name as string) ?? '',
            image_url: (c[opts.imageContentKey] as string) ?? (c.image_url as string) ?? '',
          };
          if (opts.withDescription) row.description = (c.description as string) ?? '';
          return row;
        }),
        total,
      };
    },
    create: async (rows, accountId) => {
      const failures: string[] = [];
      await Promise.all(
        rows.map(async (row, idx) => {
          try {
            await channelMaterialsApi.create({
              account_id: accountId,
              name: String(row.title ?? '') || '小程序卡片',
              type: 9,
              content: buildContent(row),
            });
          } catch (e) {
            failures.push(`新增第${idx + 1}条：${(e as Error)?.message ?? '失败'}`);
          }
        }),
      );
      return { failures };
    },
    update: async (rows) => {
      const failures: string[] = [];
      await Promise.all(
        rows.map(async (row, idx) => {
          try {
            await channelMaterialsApi.update(row.id as number, {
              name: String(row.title ?? '') || '小程序卡片',
              content: buildContent(row),
            });
          } catch (e) {
            failures.push(`更新第${idx + 1}条：${(e as Error)?.message ?? '失败'}`);
          }
        }),
      );
      return { failures };
    },
    remove: async (ids) => {
      await Promise.all(ids.map((id) => channelMaterialsApi.delete(id)));
    },
    deleteConfirmText: opts.deleteConfirmText,
    addText: opts.addText,
  };
}

const wxMiniProgramEditable = createMiniProgramEditable({
  imageContentKey: 'thumb_url',
  withDescription: false,
  addText: '添加小程序',
  deleteConfirmText: '确定删除选中的小程序吗？',
});

const wxkfMiniProgramEditable = createMiniProgramEditable({
  imageContentKey: 'image_url',
  withDescription: true,
  addText: '添加小程序',
  deleteConfirmText: '确定删除选中的小程序卡片吗？',
});

const fmcMiniProgramEditable = wxkfMiniProgramEditable;

/** 获客链接（type=12，content: {url, title}） */
const acquisitionLinkEditable: EditableConfig = {
  fields: [
    { prop: 'title', label: '显示标题', type: 'input', required: true },
    { prop: 'address', label: '链接地址', type: 'input', required: true },
  ],
  emptyRow: () => ({ title: '', address: '', isNew: true }),
  load: async ({ accountId, page, pageSize }) => {
    const { list, total } = await channelMaterialsApi
      .list({ account_id: accountId, type: 12, page, per_page: pageSize })
      .catch(() => ({ list: [], total: 0 }));
    return {
      list: (list as Record<string, unknown>[]).map((r) => {
        const c = parseMaterialContent(r.content);
        return {
          id: r.id,
          title: (c.title as string) ?? (r.name as string) ?? '',
          address: (c.url as string) ?? '',
        };
      }),
      total,
    };
  },
  create: async (rows, accountId) => {
    const failures: string[] = [];
    await Promise.all(
      rows.map(async (row, idx) => {
        try {
          await channelMaterialsApi.create({
            account_id: accountId,
            name: String(row.title ?? '') || '获客链接',
            type: 12,
            content: { title: row.title ?? '', url: row.address ?? '' },
          });
        } catch (e) {
          failures.push(`新增第${idx + 1}条：${(e as Error)?.message ?? '失败'}`);
        }
      }),
    );
    return { failures };
  },
  update: async (rows) => {
    const failures: string[] = [];
    await Promise.all(
      rows.map(async (row, idx) => {
        try {
          await channelMaterialsApi.update(row.id as number, {
            name: String(row.title ?? '') || '获客链接',
            content: { title: row.title ?? '', url: row.address ?? '' },
          });
        } catch (e) {
          failures.push(`更新第${idx + 1}条：${(e as Error)?.message ?? '失败'}`);
        }
      }),
    );
    return { failures };
  },
  remove: async (ids) => {
    await Promise.all(ids.map((id) => channelMaterialsApi.delete(id)));
  },
  deleteConfirmText: '确定删除选中的链接吗？',
  addText: '添加链接',
};

/**
 * 抖音商品列表每页条数上限。超过该值时抖音 goodlife 接口会「静默返回空」——
 * 不报错但 products 为空数组，因此前端必须主动把可选项收敛到 50 以内。
 */
export const DY_PRODUCT_MAX_PAGE_SIZE = 50;

/**
 * 抖音商品搜索的扫描上限。后端按商品ID / 标题搜索时最多翻这么多个商品，
 * 触顶会回传 truncated=true——此时「搜不到」只代表前 2000 个里没有，
 * 不能按「无此商品」展示，必须把搜索范围提示给用户。
 */
export const DY_PRODUCT_SCAN_LIMIT = 2000;

/**
 * 抖音商品上架状态：只有 1 是上架，其余取值（2、下架等）统一按未上架展示，
 * 避免平台新增状态码时前端漏判成上架。
 */
export const DY_PRODUCT_ONLINE_STATUS = 1;

/**
 * 支持 keyword 模糊搜索的自建物料接口（后端按 name / content.name / content.title 匹配，
 * dy-mini-list 表无 JSON 列故只匹配 title）。这些接口下的物料类型有几十个，
 * 逐个声明 searchField 过于重复，改为按 apiPath 自动启用，见 MaterialListDisplay
 */
export const KEYWORD_SEARCHABLE_APIS = [
  '/materials',
  '/channel-materials',
  '/channel-materials/dy-mini-list',
];

/** 自建物料的通用搜索项 */
export const KEYWORD_SEARCH_FIELD = { key: 'keyword', placeholder: '搜索标题 / 名称' };

/**
 * 渠道+物料类型 → 加载配置映射
 * key: `${channelKey}:${materialType}`
 * 数据字段与各对应弹框保持一致
 */
export const CHANNEL_MATERIAL_CONFIG: Record<string, ChannelMaterialConfig> = {
  // ── 抖音 ──────────────────────────────────────────────────────────────
  'dy:liuzi_card': {
    apiPath: '/channel-materials/dy-card-list',
    method: 'GET',
    extraParams: { type: 'leadCard', status: 1 },
    listKey: 'cards',
    rowKey: 'id',
    columns: [
      { prop: 'id', label: '卡片ID', width: 200 },
      { prop: 'name', label: '卡片名称' },
      { prop: 'content', label: '描述' },
    ],
  },
  /** 收集号码：内容由抖音平台固定提供，列表区只渲染静态卡片（按时段发送的下拉仍走接口） */
  'dy:collect_phone': {
    apiPath: '/channel-materials/dy-card-list',
    method: 'GET',
    extraParams: { type: 'collectPhone' },
    listKey: 'cards',
    columns: [
      { prop: 'id', label: '卡片ID', width: 120 },
      { prop: 'name', label: '卡片名称' },
      { prop: 'content', label: '描述' },
    ],
    staticPreview: {
      placeholderId: MSG_TYPE_PHONE_COLLECTION,
      lines: ['此处文案由抖音平台提供'],
      linkText: '一键发送抖音绑定手机号',
    },
  },
  'dy:product_card': {
    apiPath: '/channel-materials/dy-product-list',
    method: 'GET',
    rowKey: 'product_id',
    maxPageSize: DY_PRODUCT_MAX_PAGE_SIZE,
    searchField: { key: 'product_id', placeholder: '输入商品ID精确搜索' },
    columns: [
      { prop: 'product_id', label: '商品ID' },
      { prop: 'product_name', label: '商品名称' },
      { prop: 'price', label: '商品价格' },
      { prop: 'online_status', label: '上架状态', type: 'onlineStatus', width: 100 },
      { prop: 'image', label: '商品图片', type: 'image', width: 100 },
    ],
  },
  'dy:mini_program': {
    apiPath: '/channel-materials/dy-mini-list',
    method: 'GET',
    columns: [
      { prop: 'mini_app_id', label: '小程序ID' },
      { prop: 'title', label: '标题' },
      { prop: 'content', label: '描述' },
      { prop: 'created_at', label: '添加时间' },
    ],
    editable: dyMiniProgramEditable,
  },
  /**
   * 抖音高级预约卡：只读拉取抖音开放平台数据，不支持在系统内创建/编辑，
   * 与 dy:liuzi_card 同源、走新的统一物料查询接口（card-list）
   */
  'dy:appointment_card': {
    apiPath: '/channel-materials/card-list',
    method: 'GET',
    extraParams: { type: 'appointmentCard', status: 1 },
    columns: [
      { prop: 'id', label: '卡片ID', width: 200 },
      { prop: 'title', label: '卡片名称' },
      { prop: 'created_at', label: '添加时间' },
    ],
  },
  'dy:qa_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 7, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },
  'dy:text_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 8, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },

  // ── 抖音飞鸽（物料与抖音同源，account_id 传飞鸽账号）──────────────────
  /** 新版留资卡：发送时由抖音端替换为该组件默认文案，无素材可选 */
  'dy_feige:new_lead_card': {
    apiPath: '',
    columns: [],
    staticPreview: {
      placeholderId: MSG_TYPE_NEW_LEAD_CARD,
      lines: ['发送时替换为该组件默认文案', '使用抖音默认文案'],
    },
  },
  'dy_feige:qa_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 7, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },
  'dy_feige:text_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 8, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },

  // ── 小红书（统一端点 xhs-card-list，GET 请求）─────────────────────────
  'xhs:liuzi_card': {
    apiPath: '/channel-materials/xhs-card-list',
    method: 'GET',
    extraParams: { type: 'leadCard', status: 1 },
    columns: [
      { prop: 'id', label: '留资卡ID' },
      { prop: 'title', label: '留资卡名称' },
      { prop: 'image', label: '缩略图', type: 'image', width: 100 },
    ],
  },
  'xhs:name_card': {
    apiPath: '/channel-materials/xhs-card-list',
    method: 'GET',
    extraParams: { type: 'businessCard' },
    columns: [
      { prop: 'id', label: '名片ID' },
      { prop: 'title', label: '名片名称' },
      { prop: 'image', label: '缩略图', type: 'image', width: 100 },
    ],
  },
  'xhs:note': {
    apiPath: '/channel-materials/xhs-card-list',
    method: 'GET',
    extraParams: { type: 'note' },
    rowKey: 'note_id',
    columns: [
      { prop: 'note_id', label: '笔记ID' },
      { prop: 'title', label: '笔记名称' },
      { prop: 'cover', label: '封面', type: 'image', width: 100 },
    ],
  },
  'xhs:landing_page': {
    apiPath: '/channel-materials/xhs-card-list',
    method: 'GET',
    extraParams: { type: 'landingPage' },
    rowKey: 'page_id',
    columns: [
      { prop: 'page_id', label: '落地页ID' },
      { prop: 'title', label: '落地页名称' },
    ],
  },
  'xhs:trade_card': {
    apiPath: '/channel-materials/xhs-card-list',
    method: 'GET',
    extraParams: { type: 'transactionCard' },
    columns: [
      { prop: 'id', label: '交易卡ID' },
      { prop: 'title', label: '交易卡名称' },
      { prop: 'image', label: '缩略图', type: 'image', width: 100 },
    ],
  },
  'xhs:text_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 8, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },

  // ── 快手 ──────────────────────────────────────────────────────────────
  'kuaishou:liuzi_card': {
    apiPath: '/channel-materials/ks-lead-card-list',
    method: 'GET',
    extraParams: { status: 1 },
    columns: [
      { prop: 'id', label: 'ID' },
      { prop: 'card_name', label: '卡片名称' },
      { prop: 'type', label: '类型' },
    ],
  },
  'kuaishou:card': {
    apiPath: '/channel-materials/ks-store-card-list',
    method: 'GET',
    columns: [
      { prop: 'id', label: 'ID' },
      { prop: 'card_name', label: '卡片名称' },
    ],
  },
  'kuaishou:text_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 8, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },

  // ── 微信公众号 ────────────────────────────────────────────────────────
  'wx:article': {
    apiPath: '/channel-materials/wx-article-list',
    method: 'GET',
    columns: [
      { prop: 'title', label: '图文标题' },
      { prop: 'description', label: '图文描述' },
      { prop: 'url', label: '图文链接' },
      { prop: 'image_url', label: '图文封面', type: 'image', width: 100 },
    ],
    editable: wxArticleEditable,
  },
  'wx:mini_program': {
    apiPath: '/channel-materials',
    method: 'GET',
    extraParams: { type: 9 },
    pageSizeKey: 'per_page',
    transform: transformMiniProgram,
    columns: [
      { prop: 'app_id', label: 'appid' },
      { prop: 'page_path', label: '小程序路径' },
      { prop: 'card_name', label: '卡片标题' },
      { prop: 'image_url', label: '上传图片', type: 'image', width: 100 },
    ],
    editable: wxMiniProgramEditable,
  },
  'wx:qa_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 7, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },
  'wx:text_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 8, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },

  // ── 企业微信（个人号）──────────────────────────────────────────────────
  /**
   * 图文消息：企微为系统内自建物料（非微信素材库），走通用渠道物料接口 type=5，
   * 字段与微信公众号图文一致，复用 wxArticleEditable 做内联增删改
   */
  'qywx:article': {
    apiPath: '/channel-materials',
    method: 'GET',
    extraParams: { type: 5 },
    pageSizeKey: 'per_page',
    transform: transformArticle,
    columns: [
      { prop: 'title', label: '图文标题' },
      { prop: 'description', label: '图文描述' },
      { prop: 'url', label: '图文链接' },
      { prop: 'image_url', label: '图文封面', type: 'image', width: 100 },
    ],
    editable: wxArticleEditable,
  },
  /** 小程序：与微信公众号同源（见 OperationArea 中企微复用 wxMiniModal） */
  'qywx:mini_program': {
    apiPath: '/channel-materials',
    method: 'GET',
    extraParams: { type: 9 },
    pageSizeKey: 'per_page',
    transform: transformMiniProgram,
    columns: [
      { prop: 'app_id', label: 'appid' },
      { prop: 'page_path', label: '小程序路径' },
      { prop: 'card_name', label: '卡片标题' },
      { prop: 'image_url', label: '上传图片', type: 'image', width: 100 },
    ],
    editable: wxMiniProgramEditable,
  },
  /** 文本菜单走通用素材接口，与其他渠道同源；发送时按「其他 → TEXT」降级为纯文本 */
  'qywx:text_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 8, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },

  // ── 微信客服 ──────────────────────────────────────────────────────────
  'wxkf:mini_program': {
    apiPath: '/channel-materials',
    method: 'GET',
    extraParams: { type: 9 },
    pageSizeKey: 'per_page',
    transform: transformMiniProgram,
    columns: [
      { prop: 'app_id', label: 'appid' },
      { prop: 'page_path', label: '小程序路径' },
      { prop: 'card_name', label: '卡片名称', width: 120 },
      { prop: 'description', label: '卡片描述', width: 120 },
      { prop: 'image_url', label: 'LOGO', type: 'image', width: 80 },
    ],
    editable: wxkfMiniProgramEditable,
  },
  'wxkf:qa_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 7, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },
  'wxkf:text_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 8, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },
  'wxkf:acquisition_link': {
    apiPath: '/channel-materials',
    method: 'GET',
    extraParams: { type: 12 },
    pageSizeKey: 'per_page',
    transform: transformAcquisitionLink,
    columns: [
      { prop: 'title', label: '显示标题' },
      { prop: 'address', label: '链接地址' },
    ],
    editable: acquisitionLinkEditable,
  },

  // ── 网站咨询 ──────────────────────────────────────────────────────────
  'web:liuzi_card': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 6, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformWebLeadCard,
    columns: [{ prop: 'name', label: '留资卡名称' }],
  },
  'web:qa_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 7, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },
  'web:text_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 8, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },

  // ── 通用（communal：占位符话术池 / 问答配置等无渠道场景，与网站共用 /materials） ──
  'communal:qa_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 7, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },
  'communal:text_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 8, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },

  // ── 腾讯咨询页（接口与微信客服一致） ──────────────────────────────────
  'fmc:mini_program': {
    apiPath: '/channel-materials',
    method: 'GET',
    extraParams: { type: 9 },
    pageSizeKey: 'per_page',
    transform: transformMiniProgram,
    columns: [
      { prop: 'app_id', label: 'appid' },
      { prop: 'page_path', label: '小程序路径' },
      { prop: 'card_name', label: '卡片名称' },
      { prop: 'description', label: '卡片描述' },
      { prop: 'image_url', label: 'LOGO', type: 'image', width: 100 },
    ],
    editable: fmcMiniProgramEditable,
  },
  'fmc:qa_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 7, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },
  'fmc:text_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 8, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },
  'fmc:acquisition_link': {
    apiPath: '/channel-materials',
    method: 'GET',
    extraParams: { type: 12 },
    pageSizeKey: 'per_page',
    transform: transformAcquisitionLink,
    columns: [
      { prop: 'title', label: '显示标题' },
      { prop: 'address', label: '链接地址' },
    ],
    editable: acquisitionLinkEditable,
  },

  // ── 百度营销 ──────────────────────────────────────────────────────────
  'baidu:text_menu': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 8, status: 1 },
    pageSizeKey: 'per_page',
    transform: transformMaterial,
    columns: [{ prop: 'title', label: '素材名称' }],
  },

  // ── 全渠道通用（key 以 * 开头，见 getMaterialConfig）─────────────────────
  /**
   * 图片：素材来自图片库（通用素材 type=2），与渠道、账号都无关，
   * 因此不按渠道逐个配置；落库时 account_id=0，请求必须跳过 account_id 才查得到。
   */
  '*:image': {
    apiPath: '/materials',
    method: 'GET',
    extraParams: { type: 2, status: 1 },
    pageSizeKey: 'per_page',
    skipAccountId: true,
    columns: [{ prop: 'name', label: '图片名称' }],
  },
};

/**
 * 取渠道 + 物料类型对应的加载配置。
 *
 * 先按 `渠道:类型` 精确匹配，没有再回落到 `*:类型` 的全渠道通用配置——
 * 图片这类素材各渠道数据源完全相同，没必要在十几个渠道下重复同一份配置。
 */
export function getMaterialConfig(
  channelKey: string,
  materialType: string,
): ChannelMaterialConfig | null {
  return (
    CHANNEL_MATERIAL_CONFIG[`${channelKey}:${materialType}`] ??
    CHANNEL_MATERIAL_CONFIG[`*:${materialType}`] ??
    null
  );
}
